import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Link } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import CircleImage from '../../components/CircleImage';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';

// ICONS

import { ReactComponent as SeachIcon } from '../../assets/svg/search.svg';

// TYPES
import { DomainProfileType, IncompleteProfileAdminDashboardIncompleteProfile } from '../../api/models';

const IncompleteProfiles: React.FC = () => {
	// Check if User is Organisation Admin
	const isOrganisationAdmin = useAppSelector((state) => state.user.isOrganisationAdmin);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Set up data state
	const [incompleteProfilesData, setIncompleteProfilesData] = React.useState<
		IncompleteProfileAdminDashboardIncompleteProfile[]
	>([]);

	// Data used to search, it will not be changed and will always remain as its inital set value
	const [originalIncompleteProfilesData, setOriginalIncompleteProfilesData] = React.useState<
		IncompleteProfileAdminDashboardIncompleteProfile[]
	>([]);

	const getIncompleteProfiles = async () => {
		setIsLoading(true);

		const response = await apibridge.getIncompleteprofileAdmindashboard();

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			// Type assertion after checking isError is false
			const successResponse = response as { result?: IncompleteProfileAdminDashboardIncompleteProfile[] };
			const result = successResponse.result || [];

			setIsLoading(false);

			setIncompleteProfilesData(result);

			setOriginalIncompleteProfilesData(result);
		}
	};

	React.useEffect(() => {
		getIncompleteProfiles();
	}, []);

	const staffData = incompleteProfilesData.filter(
		(item) => item.profileType === DomainProfileType.Staff && item.isCurrentMember === true
	);

	const parentData = incompleteProfilesData.filter(
		(item) => item.profileType === DomainProfileType.Parent && item.isCurrentMember === true
	);

	const visitorsData = incompleteProfilesData.filter(
		(item) => item.profileType === DomainProfileType.Visitor && item.isCurrentMember === true
	);

	// State to control active tab and active content
	const [activeTab, setActiveTab] = React.useState<string>('tab1');
	const [searchValue, setSearchValue] = React.useState<string>('');

	// Filter Function
	const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newSearchValue = e.target.value;

		if (!newSearchValue) {
			setIncompleteProfilesData(originalIncompleteProfilesData);
		} else {
			const currentOriginalIncompleteProfilesData = originalIncompleteProfilesData.filter(
				(item) => item.isCurrentMember === true
			);

			const newSearchResult = currentOriginalIncompleteProfilesData.filter(
				(data) =>
					(data.firstName && data.firstName.toLowerCase().includes(newSearchValue.toLowerCase())) ||
					(data.lastName && data.lastName.toLowerCase().includes(newSearchValue.toLowerCase()))
			);

			setIncompleteProfilesData(newSearchResult);
		}

		setSearchValue(newSearchValue);
	};

	return (
		<div className="page-general-layout families-page">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<nav className="page-header-section families-page">
									<div className="header-left">
										<Button
											onClick={() => setActiveTab('tab1')}
											className={`staff-category pdl-0 ${activeTab === 'tab1' ? 'active' : ''}`}
										>
											Staff & Volunteers
										</Button>

										<Button
											onClick={() => setActiveTab('tab2')}
											className={`staff-category ${activeTab === 'tab2' ? 'active' : ''}`}
										>
											Parent Volunteers
										</Button>

										<Button
											onClick={() => setActiveTab('tab3')}
											className={`staff-category ${activeTab === 'tab3' ? 'active' : ''}`}
										>
											Visitors
										</Button>
									</div>

									<div className="header-right">
										<div className="search-container">
											<SeachIcon className="search-icon" />

											<input onChange={onChangeHandler} type="text" placeholder="Search" value={searchValue} />
										</div>
									</div>
								</nav>

								<section className="info-section mtop-40 mbtom-30">
									{activeTab === 'tab1' && (
										<ul>
											{isOrganisationAdmin &&
												staffData &&
												staffData.length > 0 &&
												staffData.map((item) => {
													return (
														<li key={item.id} className="row-container">
															<div className="row detail-row">
																<div className="col-auto pdl-0 pdr-0 img-container">
																	<CircleImage
																		firstName={item.firstName}
																		lastName={item.lastName}
																		src={item.profileImage}
																		isProfileInComplete={item.needsAttention}
																	/>
																</div>

																<div className="col-md-auto full-width info-container align-self-center">
																	<div className="row">
																		<div className="col-lg-4">
																			{item.firstName} {item.lastName}
																		</div>

																		<div className="col-auto">
																			<span className="font-semibold text-grey-4">{item.role}</span>
																		</div>
																	</div>
																</div>

																<div className="col-auto pdr-0 link-group">
																	<Link className="btn-text mtop-0 pdr-0 attention" to={`/staff/${item.id}`}>
																		Action required
																	</Link>
																</div>
															</div>
														</li>
													);
												})}
										</ul>
									)}

									{activeTab === 'tab2' && (
										<ul>
											{isOrganisationAdmin &&
												parentData &&
												parentData.length > 0 &&
												parentData.map((item) => {
													return (
														<li key={item.id} className="row-container">
															<div className="row detail-row">
																<div className="col-auto pdl-0 pdr-0 img-container">
																	<CircleImage
																		firstName={item.firstName}
																		lastName={item.lastName}
																		src={item.profileImage}
																		isProfileInComplete={item.needsAttention}
																	/>
																</div>

																<div className="col-md-auto full-width info-container align-self-center">
																	<div className="row">
																		<div className="col-lg-4">
																			{item.firstName} {item.lastName}
																		</div>

																		<div className="col-auto">
																			<span className="font-semibold text-grey-4">{item.role}</span>
																		</div>
																	</div>
																</div>

																<div className="col-auto pdr-0 link-group">
																	<Link className="btn-text mtop-0 pdr-0 attention" to={`/families/${item.id}`}>
																		Action required
																	</Link>
																</div>
															</div>
														</li>
													);
												})}
										</ul>
									)}

									{activeTab === 'tab3' && (
										<ul>
											{isOrganisationAdmin &&
												visitorsData &&
												visitorsData.length > 0 &&
												visitorsData.map((item) => {
													return (
														<li key={item.id} className="row-container">
															<div className="row detail-row">
																<div className="col-auto pdl-0 pdr-0 img-container">
																	<CircleImage
																		firstName={item.firstName}
																		lastName={item.lastName}
																		src={item.profileImage}
																		isProfileInComplete={item.needsAttention}
																	/>
																</div>

																<div className="col-md-auto full-width info-container align-self-center">
																	<div className="row">
																		<div className="col-lg-4">
																			{item.firstName} {item.lastName}
																		</div>

																		<div className="col-auto">
																			<span className="font-semibold text-grey-4">{item.role}</span>
																		</div>
																	</div>
																</div>

																<div className="col-auto pdr-0 link-group">
																	<Link className="btn-text pdr-0 attention" to={`/visitors/${item.id}`}>
																		Action required
																	</Link>
																</div>
															</div>
														</li>
													);
												})}
										</ul>
									)}
								</section>

								<ScrollToTopButton />
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default IncompleteProfiles;
